<script lang="ts">
interface TableColumn {
  title: string;
  filter?:
    | {
        type: FilterType;
        placeholder?: string;
      }
    | { type: 'RESET_FILTERS_BUTTON' };
  alignRight?: boolean;
  noSort?: boolean;
}

export interface TableProps {
  columns: TableColumn[];
  data: DataRow[];
  tableId?: number | string; // needed to understand when the table changes cols and data
  sortByCol?: number; // initial sort by column
  sortByColAsc?: boolean; // initial sort by column should be in Ascending order
  noFilters?: boolean;
  noFooter?: boolean;
  rowsSelectionMode?: 'single' | 'multiple';
  rowsSelectionDisabled?: boolean;
  initialRows?: number;
  loading?: boolean;
}
</script>

<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import TablePagination from '@/table/ElTablePagination.vue';
import CustomTransition from '@/_CustomTransition.vue';
import ElInputCheckbox from '@/forms/ElInputCheckbox.vue';
import ElInputSelect from '@/forms/ElInputSelect.vue';
import ElInputDate from '@/forms/ElInputDate.vue';
import ElInputText from '@/forms/ElInputText.vue';
import ElSpinner from '@/ElSpinner.vue';
import ElTableCell, { getCellText, getCellSortableAndFilterableValue, compareSortableValues } from '@/table/ElTableCell.vue';
import ElButton from '@/ElButton.vue';
import { DataRow, FilterType } from '@/table/commonTypes';

const props = withDefaults(defineProps<TableProps>(), {
  tableId: 0,
  sortByCol: 0,
  initialRows: 15,
  sortByColAsc: false,
  rowsSelectionMode: undefined,
});

const emit = defineEmits<{
  (event: 'rows-selected', rows: DataRow[]): void;
}>();

const MAX_COLS = 10;

const currentTabDataRows = ref<DataRow[]>(props.data);

function useSorting() {
  const sortStatus = ref<{
    currentlySortColIndex: number;
    arrows: boolean[];
  }>({
    currentlySortColIndex: props.sortByCol > -1 ? props.sortByCol : -1,
    arrows: [],
  });

  onMounted(() => {
    sortStatus.value.arrows = new Array(MAX_COLS);
    if (props.sortByCol > -1) {
      sortStatus.value.arrows[props.sortByCol] = props.sortByColAsc;
    }
  });

  const sortData = (data: DataRow[]) => {
    if (data.length > 0) {
      const colIndex = sortStatus.value.currentlySortColIndex;
      const asc = sortStatus.value.arrows[colIndex];

      return data.sort((a, b) => {
        const sortValueA = getCellSortableAndFilterableValue(a.cells[colIndex]);
        const sortValueB = getCellSortableAndFilterableValue(b.cells[colIndex]);
        return asc ? compareSortableValues(sortValueA, sortValueB) : compareSortableValues(sortValueB, sortValueA);
      });
    }
    return data;
  };

  const toggleSortArrow = (colIndex: number) => {
    sortStatus.value.currentlySortColIndex = colIndex;
    sortStatus.value.arrows.splice(colIndex, 1, !sortStatus.value.arrows[colIndex]);
  };

  return {
    sortStatus,
    sortData,
    toggleSortArrow,
  };
}

function useFilters() {
  const filterStatus = ref<
    {
      textSearch: string;
      dateFrom: number;
    }[]
  >([]);

  onMounted(() => {
    filterStatus.value = new Array(MAX_COLS);
    for (let i = 0; i < MAX_COLS; i++) {
      filterStatus.value.splice(i, 1, {
        textSearch: '',
        dateFrom: 0,
      });
    }
  });

  watch(
    () => props.tableId,
    () => resetFilter(),
  );

  const filteredData = computed((): typeof props.data => {
    let data = props.data.slice();

    if (filterStatus.value.length > 0 && data.length > 0) {
      for (let i = 0; i < props.columns.length; i++) {
        const colToFilter = props.columns[i];
        const currentFilter = filterStatus.value[i];

        if (colToFilter.filter?.type === 'FREE_SEARCH') {
          if (currentFilter.textSearch) {
            const searchValues = currentFilter.textSearch.trim().toLowerCase().split(' ');
            data = data.filter(d => {
              const cellValue = getCellSortableAndFilterableValue(d.cells[i]).toLowerCase();
              return searchValues.some(searchValue => cellValue && cellValue.includes(searchValue));
            });
          }
        } else if (colToFilter.filter?.type === 'MULTI_VALUE') {
          if (currentFilter.textSearch) {
            data = data.filter(d => currentFilter.textSearch === getCellSortableAndFilterableValue(d.cells[i]));
          }
        } else if (colToFilter.filter?.type === 'DATE_RANGE') {
          if (currentFilter.dateFrom > 0) {
            data = data.filter(d => parseInt(getCellSortableAndFilterableValue(d.cells[i])) >= currentFilter.dateFrom);
          }
        }
      }

      if (sortStatus.value.currentlySortColIndex > -1) {
        return sortData(data);
      }
    }
    return data;
  });

  const resetFilter = () => {
    filterStatus.value.forEach(f => {
      f.textSearch = '';
      f.dateFrom = 0;
    });
  };

  const findFilterMultiValuesOptions = (colIndex: number): InstanceType<typeof ElInputSelect>['$props']['options'] => {
    const mainTextValues = props.data
      .filter(d => {
        const currentCell = d.cells[colIndex];
        return getCellText(currentCell);
      })
      .map(d => getCellText(d.cells[colIndex]) || '');
    return [...new Set(mainTextValues)].map(v => ({
      value: v,
      label: v,
    }));
  };
  return {
    filterStatus,
    filteredData,
    resetFilter,
    findFilterMultiValuesOptions,
  };
}

function useRowsSelection() {
  const selectedRows = ref<typeof props.data>([]);

  const emitRowsSelected = () => {
    emit('rows-selected', selectedRows.value);
  };

  const rowsSelected = (rows: DataRow[]) => {
    selectRows(rows.map(r => r.id));
  };

  const rowUnselected = (rowId: string) => {
    const index = selectedRows.value.findIndex(r => r.id === rowId);
    selectedRows.value.splice(index, 1);
    emitRowsSelected();
  };

  // select the rows with the given ids, possibly in addition to the rows that are already selected
  const selectRows = async (rowsIds: string[], shouldFilterOnCurrentTabData: boolean = true) => {
    if (props.rowsSelectionMode === 'single') {
      selectedRows.value = [];
      await nextTick();
    }

    const rowsToSelect = shouldFilterOnCurrentTabData
      ? currentTabDataRows.value.filter(r => rowsIds.includes(r.id))
      : [...props.data].filter(r => rowsIds.includes(r.id));

    if (props.rowsSelectionMode === 'single') {
      rowsToSelect.length = Math.min(1, rowsToSelect.length);
    }

    for (const row of rowsToSelect) {
      if (!selectedRows.value.some(r => r.id === row.id)) {
        selectedRows.value.push(row);
      }
    }

    emitRowsSelected();
  };

  const unselectRows = (rowsIds: string[]) => {
    selectedRows.value = selectedRows.value.filter(r => !rowsIds.includes(r.id));
    emitRowsSelected();
  };

  const selectAllRows = () => {
    selectedRows.value = [...props.data];
    emitRowsSelected();
  };

  const unselectAllRows = () => {
    selectedRows.value = [];
    emitRowsSelected();
  };

  const getDataRows = (): DataRow[] => currentTabDataRows.value;
  const getSelectedRows = (): DataRow[] => selectedRows.value;

  const allRowsSelected = computed(() => {
    return selectedRows.value.length > 0 && selectedRows.value.length === currentTabDataRows.value.length;
  });

  return {
    selectedRows,
    rowsSelected,
    rowUnselected,
    selectRows,
    unselectRows,
    selectAllRows,
    unselectAllRows,
    getDataRows,
    getSelectedRows,
    allRowsSelected,
  };
}

const {
  selectedRows,
  rowsSelected,
  rowUnselected,
  selectRows,
  unselectRows,
  selectAllRows,
  unselectAllRows,
  getDataRows,
  getSelectedRows,
  allRowsSelected,
} = useRowsSelection();
const { filterStatus, filteredData, resetFilter, findFilterMultiValuesOptions } = useFilters();
const { sortStatus, sortData, toggleSortArrow } = useSorting();

defineExpose({
  filteredData,
  selectRows,
  unselectRows,
  selectAllRows,
  unselectAllRows,
  getDataRows,
  getSelectedRows,
});
</script>

<template>
  <div class="flex flex-col">
    <div class="overflow-x-auto">
      <div class="inline-block min-w-full bg-white align-middle">
        <table class="min-w-full">
          <thead>
            <tr class="bg-neutral-surface-raised font-semibold">
              <th
                v-if="rowsSelectionMode"
                scope="col"
                class="bg-neutral-surface-raised text-neutral-darker flex justify-center px-3 py-3 text-xs uppercase leading-4 tracking-wider"
                :class="{ 'cursor-pointer': rowsSelectionMode === 'multiple' }"
              >
                <ElInputCheckbox
                  v-if="rowsSelectionMode === 'multiple'"
                  :model-value="allRowsSelected"
                  :disabled="rowsSelectionDisabled"
                  size="xxs"
                  @click="() => (allRowsSelected ? unselectAllRows() : rowsSelected(currentTabDataRows))"
                />
                <div v-else class="w-7" />
              </th>
              <th
                v-for="(col, index) in props.columns"
                :key="index"
                scope="col"
                class="bg-neutral-surface-raised text-neutral-darker whitespace-nowrap rounded-none px-3 py-3 text-xs font-semibold uppercase leading-4 tracking-wider"
                :class="[col.alignRight ? 'text-right' : 'text-left', !col.noSort ? 'cursor-pointer' : 'cursor-default']"
                :title="!col.noSort ? (sortStatus.arrows[index] ? 'Ordine crescente' : 'Ordine decrescente') : 'Colonna non ordinabile'"
                @click="!col.noSort && toggleSortArrow(index)"
              >
                {{ col.title }}

                <!-- //////////////////// SORT //////////////////////////// -->
                <svg
                  v-if="!col.noSort"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  class="ml-1 inline-flex h-3 w-3"
                  fill="currentColor"
                >
                  <path
                    v-if="sortStatus.arrows[index]"
                    d="M13 5.41V21a1 1 0 0 1-2 0V5.41l-5.3 5.3a1 1 0 1 1-1.4-1.42l7-7a1 1 0 0 1 1.4 0l7 7a1 1 0 1 1-1.4 1.42L13 5.4z"
                  />
                  <path
                    v-else
                    d="M11 18.59V3a1 1 0 0 1 2 0v15.59l5.3-5.3a1 1 0 0 1 1.4 1.42l-7 7a1 1 0 0 1-1.4 0l-7-7a1 1 0 0 1 1.4-1.42l5.3 5.3z"
                    title="Ordine Decrescente"
                  />
                </svg>
              </th>
            </tr>
            <!-- ///////////////////// FILTERS ///////////////////////// -->
            <tr v-if="filterStatus.length > 0 && !noFilters" class="bg-white">
              <th v-if="rowsSelectionMode" scope="col" />
              <th
                v-for="(col, index) in columns"
                :key="index"
                scope="col"
                class="text-neutral-darker px-3 py-3 text-left text-xs font-light leading-4 tracking-wider"
              >
                <!-- FILTER: TEXT SEARCH -->
                <ElInputText
                  v-if="col.filter?.type === 'FREE_SEARCH'"
                  v-model="filterStatus[index].textSearch"
                  :placeholder="col.filter?.placeholder || 'Cerca'"
                  :hidden-error-message="true"
                  :leading-icon="{
                    name: 'MagnifyingGlassIcon',
                  }"
                />

                <!-- FILTER: DATE RANGE -->
                <div v-else-if="col.filter?.type === 'DATE_RANGE'" class="flex flex-col">
                  <ElInputDate v-model="filterStatus[index].dateFrom" :hidden-error-message="true" />
                </div>

                <!-- FILTER: MULTIPLE VALUES -->
                <ElInputSelect
                  v-else-if="col.filter?.type === 'MULTI_VALUE'"
                  v-model="filterStatus[index].textSearch"
                  class="flex flex-col"
                  type="autocomplete"
                  :options="findFilterMultiValuesOptions(index)"
                  :hidden-error-message="true"
                />

                <!-- TODO: change with new button when it'll be available in version 2 -->
                <!-- FILTER: RESET -->

                <div v-else-if="col.filter?.type === 'RESET_FILTERS_BUTTON'" class="flex justify-end">
                  <ElButton label="Azzera filtri" variant="tertiary" size="xs" @click="resetFilter" />
                </div>
              </th>
            </tr>
          </thead>
          <CustomTransition name="fade">
            <tbody v-if="props.loading" key="loading">
              <tr>
                <td :colspan="props.columns.length + 1">
                  <ElSpinner label="Caricamento dati..." class="p-4" :extra-loading-msg-after-seconds="3" />
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="currentTabDataRows.length > 0" key="tableFull" class="bg-white">
              <tr
                v-for="row in currentTabDataRows"
                :key="row.id"
                class="transition duration-150 ease-in-out border-t border-neutral-surface"
                :class="{
                  'bg-neutral-surface-raised': rowsSelectionMode && selectedRows.find(x => x.id === row.id),
                }"
                data-cy="table-row"
              >
                <!-- ROWS SELECTION -->
                <td v-if="rowsSelectionMode" class="flex h-20 items-center justify-center py-3">
                  <ElInputCheckbox
                    :model-value="!!selectedRows.find(r => r.id === row.id)"
                    :disabled="rowsSelectionDisabled"
                    size="xxs"
                    @update:model-value="val => (val ? rowsSelected([row]) : rowUnselected(row.id))"
                  />
                </td>

                <!-- ///////////////////// DATA CELLS ///////////////////////// -->
                <td
                  v-for="(cell, i) in row.cells"
                  :key="i"
                  class="max-w-sm px-2.5 py-2.5"
                  :data-cy="cell.type === 'default' && cell.mainText.trim().replaceAll(' ', '').replaceAll(',', '')"
                >
                  <!-- TODO data-cy should stay inside ElTableCell -->
                  <ElTableCell :cell="cell" />
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="currentTabDataRows.length === 0" key="tableEmpty">
              <tr>
                <td :colspan="props.columns.length + 1" class="p-4 font-light text-neutral-darker">nessun dato trovato</td>
              </tr>
            </tbody>
          </CustomTransition>
        </table>
      </div>
    </div>

    <!-- Footer -->
    <TablePagination
      v-model:current-tab-rows="currentTabDataRows"
      :rows="filteredData"
      :rows-per-page="props.initialRows"
      :hide-footer="props.noFooter"
    />
  </div>
</template>
